package com.speedsoftware.nibbler.client

import com.speedsoftware.nibbler.models.*
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpResponseValidator
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.request
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json

class NibblerApiClient(
    var baseUrl: String = "http://localhost:8080",
    var authToken: String? = null,
    /** Web: keep the session in an HttpOnly cookie that the browser manages; no token is ever returned to the app. */
    var cookieSessions: Boolean = false,
    /** Native apps (Android, iOS) set this so the server gives them a long, sliding session. */
    var mobileClient: Boolean = false
) {
    /** Called when the server rejects an authenticated request because the session is gone (expired, idle, logged out elsewhere). */
    var onUnauthorized: (() -> Unit)? = null

    private val client = HttpClient {
        HttpResponseValidator {
            validateResponse { response ->
                val path = response.request.url.encodedPath
                // Wrong passwords / codes also answer 401, but those are handled by the login screen itself
                if (response.status.value == 401 && !path.startsWith("/api/auth/login")) onUnauthorized?.invoke()
            }
        }
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
                prettyPrint = true
            })
        }
        defaultRequest {
            contentType(ContentType.Application.Json)
            if (cookieSessions) header("X-Auth-Mode", "cookie")
            if (mobileClient) header("X-Client", "mobile")
        }
    }

    /** Plain client without JSON defaults so cross-origin image GETs stay CORS "simple" requests. */
    private val plainClient = HttpClient()

    /** Downloads the raw bytes of an image URL (avatars, project images). */
    suspend fun fetchImageBytes(url: String): Result<ByteArray> = runCatching {
        val response = plainClient.get(url)
        if (response.status.value !in 200..299) error("HTTP ${response.status.value}")
        response.body<ByteArray>()
    }

    private fun addAuth(builder: io.ktor.client.request.HttpRequestBuilder) {
        authToken?.let {
            builder.header("Authorization", "Bearer $it")
        }
    }

    // --- Authentication ---
    /** Outcome of the password step of the login. */
    sealed interface LoginOutcome {
        data class Success(val auth: AuthResponse) : LoginOutcome
        data class TwoFactorRequired(val challengeToken: String) : LoginOutcome
    }

    /** Error carrying the human-readable message of the server's [ErrorResponse]. */
    class ApiException(message: String) : Exception(message)

    private suspend inline fun <reified T> HttpResponse.bodyOrThrow(): T {
        if (status.value in 200..299) return body()
        val message = runCatching { body<ErrorResponse>().message }.getOrNull()
        throw ApiException(message ?: "Request failed (HTTP ${status.value})")
    }

    private fun LoginResponse.toOutcome(): LoginOutcome = when {
        twoFactorRequired && challengeToken != null -> LoginOutcome.TwoFactorRequired(challengeToken)
        user != null -> {
            // In cookie mode there is no token: the browser holds the session
            authToken = token
            LoginOutcome.Success(AuthResponse(token.orEmpty(), user))
        }
        else -> throw ApiException("Unexpected response from the server")
    }

    // --- Password reset ---
    suspend fun forgotPassword(email: String): Result<MessageResponse> = runCatching {
        client.post("$baseUrl/api/auth/forgot-password") {
            setBody(ForgotPasswordRequest(email))
        }.bodyOrThrow()
    }

    suspend fun resetPassword(request: ResetPasswordRequest): Result<MessageResponse> = runCatching {
        client.post("$baseUrl/api/auth/reset-password") {
            setBody(request)
        }.bodyOrThrow()
    }

    suspend fun login(request: AuthRequest): Result<LoginOutcome> = runCatching {
        client.post("$baseUrl/api/auth/login") { setBody(request) }.bodyOrThrow<LoginResponse>().toOutcome()
    }

    /** Second login step: [code] is an authenticator code or a recovery code. */
    suspend fun loginWithTwoFactor(challengeToken: String, code: String): Result<AuthResponse> = runCatching {
        val outcome = client.post("$baseUrl/api/auth/login/2fa") {
            setBody(TwoFactorLoginRequest(challengeToken, code))
        }.bodyOrThrow<LoginResponse>().toOutcome()
        (outcome as? LoginOutcome.Success)?.auth ?: throw ApiException("Unexpected response from the server")
    }

    // --- Sessions (logged-in devices) ---
    suspend fun getSessions(): Result<List<SessionDto>> = runCatching {
        client.get("$baseUrl/api/auth/sessions") { addAuth(this) }.bodyOrThrow()
    }

    suspend fun revokeSession(sessionId: String): Result<Unit> = runCatching {
        val response = client.delete("$baseUrl/api/auth/sessions/$sessionId") { addAuth(this) }
        if (response.status.value !in 200..299) response.bodyOrThrow<Unit>()
    }

    /** Ends every session except the one making the request. */
    suspend fun revokeOtherSessions(): Result<Unit> = runCatching {
        val response = client.delete("$baseUrl/api/auth/sessions") { addAuth(this) }
        if (response.status.value !in 200..299) response.bodyOrThrow<Unit>()
    }

    // --- Two-factor authentication management ---
    suspend fun setupTwoFactor(): Result<TwoFactorSetupResponse> = runCatching {
        client.post("$baseUrl/api/auth/2fa/setup") { addAuth(this) }.bodyOrThrow()
    }

    suspend fun enableTwoFactor(code: String): Result<RecoveryCodesResponse> = runCatching {
        client.post("$baseUrl/api/auth/2fa/enable") {
            addAuth(this)
            setBody(TwoFactorCodeRequest(code))
        }.bodyOrThrow()
    }

    suspend fun disableTwoFactor(password: String, code: String): Result<Unit> = runCatching {
        val response = client.post("$baseUrl/api/auth/2fa/disable") {
            addAuth(this)
            setBody(TwoFactorDisableRequest(password, code))
        }
        if (response.status.value !in 200..299) response.bodyOrThrow<Unit>()
    }

    suspend fun regenerateRecoveryCodes(code: String): Result<RecoveryCodesResponse> = runCatching {
        client.post("$baseUrl/api/auth/2fa/recovery-codes") {
            addAuth(this)
            setBody(TwoFactorCodeRequest(code))
        }.bodyOrThrow()
    }

    suspend fun adminResetTwoFactor(userId: String): Result<Unit> = runCatching {
        val response = client.post("$baseUrl/api/admin/users/$userId/reset-2fa") { addAuth(this) }
        if (response.status.value !in 200..299) response.bodyOrThrow<Unit>()
    }

    /** Ends the session on the server (and clears the session cookie in a browser). Best effort. */
    suspend fun logout(): Result<Unit> = runCatching {
        try {
            client.post("$baseUrl/api/auth/logout") { addAuth(this) }
        } finally {
            authToken = null
        }
        Unit
    }

    suspend fun getMe(): Result<UserDto> = runCatching {
        client.get("$baseUrl/api/auth/me") {
            addAuth(this)
        }.body()
    }

    // GDPR — Delete own account
    suspend fun deleteMe(): Result<Unit> = runCatching {
        val response = client.delete("$baseUrl/api/auth/me") { addAuth(this) }
        if (response.status.value !in 200..299) response.bodyOrThrow<Unit>()
    }

    suspend fun getInvite(token: String): Result<InviteDto> = runCatching {
        client.get("$baseUrl/api/auth/invite/$token").body()
    }

    suspend fun acceptInvite(request: AcceptInviteRequest): Result<AuthResponse> = runCatching {
        val response: AuthResponse = client.post("$baseUrl/api/auth/invite/accept") {
            setBody(request)
        }.body()
        authToken = response.token.takeIf { it.isNotEmpty() }
        response
    }

    // --- Admin ---
    suspend fun createInvite(request: CreateInviteRequest): Result<InviteDto> = runCatching {
        client.post("$baseUrl/api/admin/invites") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun getInvites(): Result<List<InviteDto>> = runCatching {
        client.get("$baseUrl/api/admin/invites") {
            addAuth(this)
        }.body()
    }

    suspend fun getUsers(): Result<List<UserDto>> = runCatching {
        client.get("$baseUrl/api/admin/users") {
            addAuth(this)
        }.body()
    }

    // --- Projects ---
    suspend fun getProjects(): Result<List<ProjectDto>> = runCatching {
        client.get("$baseUrl/api/projects") {
            addAuth(this)
        }.body()
    }

    suspend fun createProject(request: CreateProjectRequest): Result<ProjectDto> = runCatching {
        client.post("$baseUrl/api/projects") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun getProjectDetails(projectId: String): Result<ProjectDto> = runCatching {
        client.get("$baseUrl/api/projects/$projectId") {
            addAuth(this)
        }.body()
    }

    suspend fun updateProject(projectId: String, request: UpdateProjectRequest): Result<Unit> = runCatching {
        client.put("$baseUrl/api/projects/$projectId") {
            addAuth(this)
            setBody(request)
        }
    }

    suspend fun deleteProject(projectId: String): Result<Unit> = runCatching {
        client.delete("$baseUrl/api/projects/$projectId") {
            addAuth(this)
        }
    }

    // --- Members ---
    suspend fun addMember(projectId: String, request: AddMemberRequest): Result<ProjectMemberDto> = runCatching {
        client.post("$baseUrl/api/projects/$projectId/members") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun removeMember(projectId: String, userId: String): Result<Unit> = runCatching {
        client.delete("$baseUrl/api/projects/$projectId/members/$userId") {
            addAuth(this)
        }
    }

    // --- Boards ---
    suspend fun createBoard(projectId: String, request: CreateBoardRequest): Result<BoardDto> = runCatching {
        client.post("$baseUrl/api/projects/$projectId/boards") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun updateBoard(projectId: String, boardId: String, request: UpdateBoardRequest): Result<BoardDto> = runCatching {
        client.put("$baseUrl/api/projects/$projectId/boards/$boardId") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun deleteBoard(projectId: String, boardId: String): Result<Unit> = runCatching {
        client.delete("$baseUrl/api/projects/$projectId/boards/$boardId") {
            addAuth(this)
        }
    }

    // --- Profile ---
    suspend fun updateProfile(request: UpdateProfileRequest): Result<UserDto> = runCatching {
        client.put("$baseUrl/api/auth/me") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    // --- Task activity ---
    suspend fun getTaskLogs(taskId: String): Result<List<TaskLogDto>> = runCatching {
        client.get("$baseUrl/api/tasks/$taskId/logs") {
            addAuth(this)
        }.body()
    }

    // --- Board columns (custom statuses) ---
    suspend fun createColumn(projectId: String, boardId: String, request: com.speedsoftware.nibbler.models.CreateColumnRequest): Result<com.speedsoftware.nibbler.models.BoardColumnDto> = runCatching {
        client.post("$baseUrl/api/projects/$projectId/boards/$boardId/columns") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun updateColumn(projectId: String, boardId: String, columnId: String, request: com.speedsoftware.nibbler.models.UpdateColumnRequest): Result<List<com.speedsoftware.nibbler.models.BoardColumnDto>> = runCatching {
        client.put("$baseUrl/api/projects/$projectId/boards/$boardId/columns/$columnId") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun deleteColumn(projectId: String, boardId: String, columnId: String, moveToColumnId: String? = null): Result<List<com.speedsoftware.nibbler.models.BoardColumnDto>> = runCatching {
        client.delete("$baseUrl/api/projects/$projectId/boards/$boardId/columns/$columnId") {
            addAuth(this)
            if (moveToColumnId != null) parameter("moveTo", moveToColumnId)
        }.body()
    }

    // --- Sprints ---
    suspend fun getSprints(projectId: String): Result<List<com.speedsoftware.nibbler.models.SprintDto>> = runCatching {
        client.get("$baseUrl/api/projects/$projectId/sprints") {
            addAuth(this)
        }.body()
    }

    suspend fun createSprint(projectId: String, request: com.speedsoftware.nibbler.models.CreateSprintRequest): Result<com.speedsoftware.nibbler.models.SprintDto> = runCatching {
        client.post("$baseUrl/api/projects/$projectId/sprints") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun updateSprint(projectId: String, sprintId: String, request: com.speedsoftware.nibbler.models.UpdateSprintRequest): Result<com.speedsoftware.nibbler.models.SprintDto> = runCatching {
        client.put("$baseUrl/api/projects/$projectId/sprints/$sprintId") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun deleteSprint(projectId: String, sprintId: String): Result<Unit> = runCatching {
        client.delete("$baseUrl/api/projects/$projectId/sprints/$sprintId") {
            addAuth(this)
        }
    }

    // --- Tasks ---
    suspend fun createTask(projectId: String, boardId: String, request: CreateTaskRequest): Result<TaskDto> = runCatching {
        client.post("$baseUrl/api/projects/$projectId/boards/$boardId/tasks") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun updateTask(taskId: String, request: UpdateTaskRequest): Result<TaskDto> = runCatching {
        client.patch("$baseUrl/api/tasks/$taskId") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    /** Drops a task into [columnId], right before [beforeTaskId] (end of the column when null). */
    suspend fun moveTask(taskId: String, columnId: String, beforeTaskId: String?): Result<TaskDto> = runCatching {
        client.post("$baseUrl/api/tasks/$taskId/move") {
            addAuth(this)
            setBody(MoveTaskRequest(columnId, beforeTaskId))
        }.bodyOrThrow()
    }

    // --- Comments ---
    suspend fun getComments(taskId: String): Result<List<CommentDto>> = runCatching {
        client.get("$baseUrl/api/tasks/$taskId/comments") { addAuth(this) }.bodyOrThrow()
    }

    suspend fun addComment(taskId: String, content: String): Result<CommentDto> = runCatching {
        client.post("$baseUrl/api/tasks/$taskId/comments") {
            addAuth(this)
            setBody(CreateCommentRequest(content))
        }.bodyOrThrow()
    }

    suspend fun deleteComment(taskId: String, commentId: String): Result<Unit> = runCatching {
        val response = client.delete("$baseUrl/api/tasks/$taskId/comments/$commentId") { addAuth(this) }
        if (response.status.value !in 200..299) response.bodyOrThrow<Unit>()
    }

    suspend fun deleteTask(taskId: String): Result<Unit> = runCatching {
        client.delete("$baseUrl/api/tasks/$taskId") {
            addAuth(this)
        }
    }

    // --- Documents ---
    suspend fun getDocuments(projectId: String): Result<List<DocumentDto>> = runCatching {
        client.get("$baseUrl/api/projects/$projectId/documents") {
            addAuth(this)
        }.body()
    }

    suspend fun getDocument(projectId: String, docId: String): Result<DocumentDto> = runCatching {
        client.get("$baseUrl/api/projects/$projectId/documents/$docId") {
            addAuth(this)
        }.body()
    }

    suspend fun createDocument(projectId: String, request: CreateDocumentRequest): Result<DocumentDto> = runCatching {
        client.post("$baseUrl/api/projects/$projectId/documents") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun updateDocument(projectId: String, docId: String, request: UpdateDocumentRequest): Result<DocumentDto> = runCatching {
        client.put("$baseUrl/api/projects/$projectId/documents/$docId") {
            addAuth(this)
            setBody(request)
        }.body()
    }

    suspend fun deleteDocument(projectId: String, docId: String): Result<Unit> = runCatching {
        client.delete("$baseUrl/api/projects/$projectId/documents/$docId") {
            addAuth(this)
        }
    }
}
